import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useFormik } from 'formik';
import { CncReport } from '@gameonly/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import Button, { WhiteButton } from '../ui/Button';

interface EditReportModalProps {
  open: boolean;
  report: CncReport;
  onClose: () => void;
  onSave: (values: CncReport) => void;
}

const nameAndTypeSchema = yup.object({
  name: yup.string().required('Name is required'),
  type: yup.string().required('Report type is required'),
});

export const fullValidationSchema = yup.object({
  name: yup.string().required('Name is required'),
  type: yup.string().required('Report type is required'),
  foreignCoproduction: yup
    .string()
    .oneOf(['OUI', 'NON'])
    .required('Champ obligatoire'),
  geographicalPerimeter: yup.string().required('Champ obligatoire'),
  duration: yup.number().required('Champ obligatoire'),
  technic: yup
    .string()
    .required('Champ obligatoire')
    .oneOf(['2D', '3D', '3DTR', 'A']),
  budget: yup.number().required('Champ obligatoire'),
  genre: yup
    .string()
    .required('Champ obligatoire')
    .oneOf(['AAV', 'ROL', 'RFX', 'GES', 'A']),
  typology: yup
    .string()
    .required('Champ obligatoire')
    .oneOf(['C', 'PC', 'M', 'A']),
  sector: yup
    .string()
    .required('Champ obligatoire')
    .oneOf(['Numérique', 'Autre']),
  estimatedCncDeposit: yup.date().required('Champ obligatoire'),
  budgetConsidered: yup.number().required('Champ obligatoire'),
  exploitationMode: yup.string().oneOf(['SC', 'LiveOps', 'A']).required(),
});

const EditReportModal = ({
  open,
  report,
  onClose,
  onSave,
}: EditReportModalProps) => {
  const { t } = useTranslation(['cnc-reports', 'common']);

  const formik = useFormik({
    initialValues: report,
    validationSchema: nameAndTypeSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values) => {
      fullValidationSchema
        .validate(values, { abortEarly: false })
        .then(() => {
          onSave({ ...values, isInformationComplete: true });
        })
        .catch(() => {
          onSave({ ...values, isInformationComplete: false });
        });

      onClose();
    },
  });

  const handleChange = (e: React.ChangeEvent<any>) => {
    formik.handleChange(e);
    nameAndTypeSchema.validateAt(e.target.name, formik.values).catch((err) => {
      formik.setFieldError(e.target.name, err.message);
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>
          <Typography variant="h5" gutterBottom>
            {t('cnc-reports:edit-modal.title')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* General Information */}
          <Typography variant="h6" gutterBottom>
            {t('cnc-reports:edit-modal.sections.general-information')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                id="name"
                name="name"
                label={t('cnc-reports:edit-modal.name')}
                value={formik.values.name}
                onChange={handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl component="fieldset">
                <FormLabel>{t('cnc-reports:edit-modal.type')}</FormLabel>
                <RadioGroup
                  id="type"
                  name="type"
                  value={formik.values.type}
                  onChange={handleChange}
                  row
                >
                  <FormControlLabel
                    value="forecast"
                    control={<Radio />}
                    label={t('cnc-reports:edit-modal.type-forecast')}
                  />
                  <FormControlLabel
                    value="realized"
                    control={<Radio />}
                    label={t('cnc-reports:edit-modal.type-realized')}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>

          {/* Technical Details */}
          <Typography variant="h6" gutterBottom>
            {t('cnc-reports:edit-modal.sections.technical-details')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="foreign-coproduction-label">
                  {t('cnc-reports:edit-modal.foreignCoproduction')}
                </InputLabel>
                <Select
                  labelId="foreign-coproduction-label"
                  label={t('cnc-reports:edit-modal.foreignCoproduction')}
                  id="foreignCoproduction"
                  name="foreignCoproduction"
                  value={formik.values.foreignCoproduction}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.foreignCoproduction &&
                    Boolean(formik.errors.foreignCoproduction)
                  }
                >
                  <MenuItem value="OUI">{t('common:yes')}</MenuItem>
                  <MenuItem value="NON">{t('common:no')}</MenuItem>
                </Select>
                {formik.touched.foreignCoproduction &&
                  formik.errors.foreignCoproduction && (
                    <p style={{ color: 'red' }}>
                      {formik.errors.foreignCoproduction}
                    </p>
                  )}
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="foreign-coproduction-label">
                  {t('cnc-reports:edit-modal.sector')}
                </InputLabel>
                <Select
                  labelId="sector-label"
                  id="sector"
                  name="sector"
                  label={t('cnc-reports:edit-modal.sector')}
                  value={formik.values.sector}
                  onChange={formik.handleChange}
                  error={formik.touched.sector && Boolean(formik.errors.sector)}
                >
                  <MenuItem value="Numérique">
                    {t('cnc-reports:edit-modal.sector-options.digital')}
                  </MenuItem>
                  <MenuItem value="Autre">
                    {t('cnc-reports:edit-modal.sector-options.other')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="foreign-coproduction-label">
                  {t('cnc-reports:edit-modal.typology')}
                </InputLabel>
                <Select
                  labelId="typology-label"
                  id="typology"
                  name="typology"
                  label={t('cnc-reports:edit-modal.typology')}
                  value={formik.values.typology}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.typology && Boolean(formik.errors.typology)
                  }
                >
                  <MenuItem value="C">
                    {t('cnc-reports:edit-modal.typology-options.c')}
                  </MenuItem>
                  <MenuItem value="PC">
                    {t('cnc-reports:edit-modal.typology-options.pc')}
                  </MenuItem>
                  <MenuItem value="M">
                    {t('cnc-reports:edit-modal.typology-options.m')}
                  </MenuItem>
                  <MenuItem value="A">
                    {t('cnc-reports:edit-modal.typology-options.a')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="geographicalPerimeter"
                name="geographicalPerimeter"
                label={t('cnc-reports:edit-modal.geographicalPerimeter')}
                value={formik.values.geographicalPerimeter}
                onChange={formik.handleChange}
                error={
                  formik.touched.geographicalPerimeter &&
                  Boolean(formik.errors.geographicalPerimeter)
                }
                helperText={
                  formik.touched.geographicalPerimeter &&
                  formik.errors.geographicalPerimeter
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="duration"
                name="duration"
                type="number"
                inputProps={{ min: 0 }}
                label={t('cnc-reports:edit-modal.duration')}
                value={formik.values.duration}
                onChange={formik.handleChange}
                error={
                  formik.touched.duration && Boolean(formik.errors.duration)
                }
                helperText={formik.touched.duration && formik.errors.duration}
                fullWidth
              />
            </Grid>
          </Grid>

          {/* Budget Information */}
          <Typography variant="h6" gutterBottom>
            {t('cnc-reports:edit-modal.sections.budget-information')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                id="budget"
                name="budget"
                type="number"
                inputProps={{ min: 0 }}
                label={t('cnc-reports:edit-modal.budget')}
                value={formik.values.budget}
                onChange={formik.handleChange}
                error={formik.touched.budget && Boolean(formik.errors.budget)}
                helperText={formik.touched.budget && formik.errors.budget}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="budgetConsidered"
                name="budgetConsidered"
                type="number"
                inputProps={{ min: 0 }}
                label={t('cnc-reports:edit-modal.budgetConsidered')}
                value={formik.values.budgetConsidered}
                onChange={formik.handleChange}
                error={
                  formik.touched.budgetConsidered &&
                  Boolean(formik.errors.budgetConsidered)
                }
                helperText={
                  formik.touched.budgetConsidered &&
                  formik.errors.budgetConsidered
                }
                fullWidth
              />
            </Grid>
          </Grid>

          {/* Advanced Details */}
          <Typography variant="h6" gutterBottom>
            {t('cnc-reports:edit-modal.sections.advanced-details')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="technic-label">
                  {t('cnc-reports:edit-modal.technic')}
                </InputLabel>
                <Select
                  labelId="technic-label"
                  label={t('cnc-reports:edit-modal.technic')}
                  id="technic"
                  name="technic"
                  value={formik.values.technic}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.technic && Boolean(formik.errors.technic)
                  }
                >
                  <MenuItem value="2D">2D</MenuItem>
                  <MenuItem value="3D">3D</MenuItem>
                  <MenuItem value="3DTR">3DTR</MenuItem>
                  <MenuItem value="A">A</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="exploitationMode-label">
                  {t('cnc-reports:edit-modal.exploitationMode')}
                </InputLabel>
                <Select
                  labelId="exploitationMode-label"
                  id="exploitationMode"
                  name="exploitationMode"
                  label={t('cnc-reports:edit-modal.exploitationMode')}
                  value={formik.values.exploitationMode}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.exploitationMode &&
                    Boolean(formik.errors.exploitationMode)
                  }
                >
                  <MenuItem value="SC">
                    {t('cnc-reports:edit-modal.exploitationMode-options.sc')}
                  </MenuItem>
                  <MenuItem value="LiveOps">
                    {t(
                      'cnc-reports:edit-modal.exploitationMode-options.liveOps',
                    )}
                  </MenuItem>
                  <MenuItem value="A">
                    {t('cnc-reports:edit-modal.exploitationMode-options.a')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="genre-label">
                  {t('cnc-reports:edit-modal.genre')}
                </InputLabel>
                <Select
                  labelId="genre-label"
                  label={t('cnc-reports:edit-modal.genre')}
                  id="genre"
                  name="genre"
                  value={formik.values.genre}
                  onChange={formik.handleChange}
                  error={formik.touched.genre && Boolean(formik.errors.genre)}
                >
                  <MenuItem value="AAV">
                    {t('cnc-reports:edit-modal.genre-options.aav')}
                  </MenuItem>
                  <MenuItem value="ROL">
                    {t('cnc-reports:edit-modal.genre-options.rol')}
                  </MenuItem>
                  <MenuItem value="RFX">
                    {t('cnc-reports:edit-modal.genre-options.rfx')}
                  </MenuItem>
                  <MenuItem value="GES">
                    {t('cnc-reports:edit-modal.genre-options.ges')}
                  </MenuItem>
                  <MenuItem value="A">
                    {t('cnc-reports:edit-modal.genre-options.a')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <InputLabel id="foreign-coproduction-label">
                {t('cnc-reports:edit-modal.estimatedCncDeposit')}
              </InputLabel>
              <TextField
                id="estimatedCncDeposit"
                name="estimatedCncDeposit"
                type="date"
                value={
                  formik.values.estimatedCncDeposit
                    ? format(
                        new Date(formik.values.estimatedCncDeposit),
                        'yyyy-MM-dd',
                      )
                    : ''
                }
                onChange={formik.handleChange}
                error={
                  formik.touched.estimatedCncDeposit &&
                  Boolean(formik.errors.estimatedCncDeposit)
                }
                helperText={
                  formik.touched.estimatedCncDeposit &&
                  typeof formik.errors.estimatedCncDeposit === 'string'
                    ? formik.errors.estimatedCncDeposit
                    : ''
                }
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <WhiteButton onClick={onClose}>{t('common:cancel')}</WhiteButton>
          <Button type="submit">
            {t('cnc-reports:edit-modal.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditReportModal;
